/* eslint-disable class-methods-use-this */

import Component from '@bythepixel/component-loader';
import groupSlugs from '@Front/constants/groupSlugs';
import { formatCurrencyInput } from '@Utility';
import GaGtag from '@Core/utility/gaGtag';

function getMessageHTML(message, level = 'notice') {
  return `<p class='tw-message tw-message--${level}'>${message}</p>`;
}

function renderMessage(container, err, level) {
  container.insertAdjacentHTML('beforeend', getMessageHTML(err, level));
}

export default class LeadForm extends Component {
  static selector = '[data-comp-lead-form]';
  static loaderPriority = 'in-view';

  constructor(containerEl) {
    super(containerEl);

    this.gaGtag = new GaGtag();

    this.form = this.$container;
    this.isTradeShowForm = this.$container.dataset.compLeadForm;
    this.isDigitalCallTree = this.$container.dataset.isDigitalCallTree === 'true' || false;

    if (this.isDigitalCallTree) {
      // when in DCT, the lead_url will be /livewire/update by default, so we update it to the current URL
      this.updateLeadUrlField();
    }

    this.allMessageContainers = this.form.querySelectorAll(
      '[data-comp-form-message]',
    );
    this.customerInterestInput = this.form.querySelector(
      '[data-customer-interest]',
    );
    this.departmentDropdown = this.form.querySelector('[data-department]');
    this.downpaymentInput = this.form.querySelector('[data-down-payment]');
    this.eventTrackerAction = this.form.getAttribute(
      'data-event-tracker-action',
    );
    this.eventTrackerLabel = this.form.getAttribute('data-event-tracker-label');
    this.eventTrackerCategory = this.form.getAttribute(
      'data-event-tracker-category',
    );
    this.formHasBeenSubmitted = false;
    this.formValidationMessage = this.form.querySelector(
      '[data-comp-form-message="form"]',
    );
    this.locationInput = this.form.querySelector('[name="location"]');
    this.selectionTypeInput = this.form.querySelector(
      '[name="selection_type"]',
    );
    this.submitButton = this.form.querySelector('[data-comp-form-submit]');
    this.googleParams = {};
    this.isFacetedSearchNoResultsPage = this.form.dataset.isFacetedSearchNoResultsPage === 'true' || false;

    this.initListeners();
  }

  clearFieldMessages() {
    this.allMessageContainers.forEach((el) => {
      while (el.firstChild) el.removeChild(el.firstChild);
    });
  }

  initListeners() {
    if (this.isTradeShowForm) {
      this.tradeShowFormListeners();
      return;
    }

    this.formSubmissionListener();
    this.downpaymentListener();
  }

  tradeShowFormListeners() {
    // Submit listener
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      this.submitButton.disabled = true;

      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute(window.Transwest.recaptchaSiteKey, {
            action: 'submit',
          })
          .then(async (token) => {
            const formData = new FormData(e.target);
            formData.append('token', token);

            const response = await fetch('/api/leads/', {
              body: formData,
              method: 'post',
            });

            this.clearFieldMessages();

            // 'Redirect' to thank you page
            if (response.status === 200) {
              // Enables the form again so that the user can resubmit
              this.submitButton.disabled = false;

              // Clear input fields before redirecting
              this.$container.reset();

              if (this.isDigitalCallTree) {
                window.Livewire?.dispatch('dct:contact-form-submitted');
                return;
              }

              window.location = '/thank-you/';
            } else if (response.status === 422 || response.status === 400) {
              // Otherwise, display validation messages
              await this.handleValidationMessages(await response.json());

              // Enables the form again so that the user can resubmit
              this.submitButton.disabled = false;
            }
          });
      });
    });
  }

  downpaymentListener() {
    if (this.downpaymentInput) {
      this.downpaymentInput.addEventListener('input', () => {
        formatCurrencyInput(this.downpaymentInput);
      });
    }
  }

  formSubmissionListener() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      this.submitButton.disabled = true;

      this.setLocation();

      // Dependent on setLocation, this needs location data
      this.populateCustomerInterestInput();

      if (this.departmentDropdown && this.departmentDropdown.value) {
        this.eventTrackerLabel = `${this.eventTrackerLabel} - ${this.departmentDropdown.value}`;
      }

      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute(window.Transwest.recaptchaSiteKey, {
            action: 'submit',
          })
          .then((token) => {
            this.submitForm(e, token);
          });
      });
    });
  }

  async handleServerResponse(response, formData) {
    this.clearFieldMessages();

    // 'Redirect' to thank you page
    if (response.status === 200) {
      let dimensionsQueryString = '';

      const body = await response.json();
      formData.append('lead_id', body.lead_id);
      if (!(body?.processing ?? true)) {
        // Only send GA event on a successful submission
        this.gaGtag.submitEvent(formData);

        // Get query string from submission event
        dimensionsQueryString = this.gaGtag.getQueryString();
      } else {
        this.gaGtag.submitSuspectEvent(formData);
      }

      this.formHasBeenSubmitted = true;

      if (this.isDigitalCallTree) {
        window.Livewire?.dispatch('dct:contact-form-submitted');
        return;
      }

      const thankYouSlug = 'thank-you';
      const groupSlug = this.form.getAttribute('data-group-slug');
      const newLocation = groupSlugs.includes(groupSlug)
        ? `/${groupSlug}/${thankYouSlug}/`
        : `/${thankYouSlug}/`;

      window.location = `${newLocation}?${dimensionsQueryString}`;
    } else if (response.status === 422 || response.status === 400) {
      // Otherwise, display validation messages
      this.handleValidationMessages(await response.json());

      // Enables the form again so that the user can resubmit
      this.submitButton.disabled = false;
    }
  }

  async handleValidationMessages(response) {
    if (response.errors) {
      Object.keys(response.errors).forEach((fieldName) => {
        response.errors[fieldName].forEach((err) => {
          renderMessage(
            this.form.querySelector(`[data-comp-form-message='${fieldName}']`),
            err,
            'error',
          );
        });
      });
    }

    renderMessage(this.formValidationMessage, response.message, 'error');
  }

  populateCustomerInterestInput() {
    if (!this.customerInterestInput) {
      return;
    }

    const { group } = this.customerInterestInput.dataset;
    this.customerInterestInput.value = `${this.location} - ${group} ${this.departmentDropdown.value}`;
  }

  setLocation() {
    // For faceted search pages, this maps the location page display name to a city
    if (
      this.selectionTypeInput.value === 'display_name'
      && this.form.querySelector('[name="stock_id"]').value === ''
    ) {
      const leadDestinationsObj = JSON.parse(
        this.form.querySelector('[name="lead_destinations"]').value,
      );
      const leadDestinations = Object.keys(leadDestinationsObj).map((key) => [
        Number(key),
        leadDestinationsObj[key],
      ]);

      for (let i = 0; i < leadDestinations.length; i++) {
        const leadDestination = leadDestinations[i][1];
        if (this.locationInput.value === leadDestination.display_name) {
          this.location = leadDestination.city;
          break;
        }
      }
    } else {
      this.location = this.locationInput.value;
    }
  }

  /**
   * Because the location option on the faceted search no results form are formatted
   * like '<group> - <city>, <state>', and we just want the city and state for Google
   */
  formatLocationForGaDimensions(location = '') {
    if (!location || location === '') return '(not set)';

    const groupInput = this.$container.querySelector('[name="group"]');
    const gdGroup = groupInput?.value ?? null;

    if (!gdGroup) {
      return '(not set)';
    }

    const [group, cityState] = location.split(' - ');
    return `${cityState} - ${gdGroup}`;
  }

  async submitForm(e, token) {
    if (this.formHasBeenSubmitted !== false) return;

    const formData = new FormData(e.target);

    if (this.isFacetedSearchNoResultsPage) {
      formData.set(
        'gd_location',
        this.formatLocationForGaDimensions(formData.get('location')),
      );
    }

    // Convert fieldset data from array to string
    formData.set(
      'financing_preference',
      formData.get('financing_preference[]'),
    );
    formData.delete('financing_preference[]');

    formData.set('location', this.location);

    formData.append('token', token);

    const response = await fetch('/api/leads/', {
      body: formData,
      method: 'post',
    });

    this.handleServerResponse(response, formData);
  }

  updateLeadUrlField() {
    const leadUrlInput = this.$container.querySelector('[name="lead_url"]');
    if (leadUrlInput) {
      leadUrlInput.value = window.location.href;
    }

    const pageUriInput = this.$container.querySelector('[name="page_uri"]');
    if (pageUriInput) {
      pageUriInput.value = window.location.pathname;
    }
  }
}
